import { AUTH } from '@util/firebase';

/** The private server receipt, not browser hint fields, authenticates a mask. */
export async function requestChatMaskProof({ chatId, senderUid, rawContent, wasRedacted }: {
  chatId: string; senderUid: string; rawContent: string; wasRedacted: boolean;
}): Promise<string | undefined> {
  if (!wasRedacted) return undefined;
  const user = AUTH.currentUser;
  if (!senderUid || user?.uid !== senderUid) throw new Error('Sign in again to send a message.');
  if (!chatId || chatId.includes('/')) throw new Error('Conversation unavailable. Please try again.');
  const controller = new AbortController();
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    const request = async () => {
      const token = await user.getIdToken();
      if (AUTH.currentUser?.uid !== senderUid) throw new Error('Authentication changed');
      const response = await fetch('/api/messages/pause', {
        method: 'POST', credentials: 'same-origin', cache: 'no-store',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({ chat_id: chatId, sender_uid: senderUid, raw_content: rawContent }),
      });
      if (!response.ok) throw new Error('Mask proof request failed');
      const result = await response.json() as { proof_id?: unknown };
      if (typeof result.proof_id !== 'string' || !/^[a-zA-Z0-9]{20}$/.test(result.proof_id)) {
        throw new Error('Invalid mask proof response');
      }
      return result.proof_id;
    };
    const deadline = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => {
        controller.abort();
        reject(new Error('Mask proof request timed out'));
      }, 3_000);
    });
    return await Promise.race([request(), deadline]);
  } catch {
    // Reuse the existing send-failure wording. Never silently send without proof.
    throw new Error('Please try again. If this continues, contact support.');
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}
